import type { ColumnDef } from '@tanstack/react-table';

import type {
  EquityRow,
  ETFRow,
  MutualFundRow,
} from '#/api/generated/fINRAMarketDataAPI.schemas';
import { equitiesColumns } from '#/components/index/EquitiesExploreTable/data';
import { etfsColumns } from '#/components/index/ETFsExploreTable/data';
import { mutualFundColumns } from '#/components/index/MutualFundsExploreTable/data';
import type { ReturnsTimeframeId } from '#/components/ui/returnsTimeframe';

// Attribution for the search-results-table ticker links. Distinct from the
// explore tables' `explore_table_ticker` so suggestion rows (section B) and
// full-results rows are reported separately. Spec row: `search_result_row`.
const SEARCH_RESULT_ROW_TRACK = {
  track: 'search_result_row',
  context: 'search_results',
} as const;

function withSorting<T>(columns: ColumnDef<T>[]) {
  return columns.map((column) => ({
    ...column,
    enableSorting: true,
  }));
}

function withoutSorting<T>(columns: ColumnDef<T>[]) {
  return columns.map((column) => ({
    ...column,
    enableSorting: false,
  }));
}

function withOptionalSorting<T>(columns: ColumnDef<T>[], sortable: boolean) {
  return sortable ? withSorting(columns) : withoutSorting(columns);
}

export function getSearchEquityColumns(
  timeframe: ReturnsTimeframeId,
  sortable = true,
): ColumnDef<EquityRow>[] {
  return withOptionalSorting(
    equitiesColumns(
      'Equities',
      [
        'ticker',
        'price',
        timeframe === '1Y' ? 'trailing_1yr_return' : 'trailing_5yr_return',
        'market_cap',
      ],
      { tickerTrack: SEARCH_RESULT_ROW_TRACK },
    ),
    sortable,
  );
}

export function getSearchEtfColumns(
  timeframe: ReturnsTimeframeId,
  sortable = true,
): ColumnDef<ETFRow>[] {
  const columnsById = new Map(
    etfsColumns('ETFs', { tickerTrack: SEARCH_RESULT_ROW_TRACK }).map(
      (column) => [column.id, column],
    ),
  );

  return withOptionalSorting(
    [
      columnsById.get('ticker'),
      columnsById.get('price'),
      columnsById.get(
        timeframe === '1Y' ? 'trailing_1yr_return' : 'return_5Y_annualized',
      ),
      columnsById.get('expense_ratio'),
    ].filter((column): column is ColumnDef<ETFRow> => column != null),
    sortable,
  );
}

export function getSearchMutualFundColumns(
  timeframe: ReturnsTimeframeId,
  sortable = true,
): ColumnDef<MutualFundRow>[] {
  return withOptionalSorting(
    mutualFundColumns(
      'Mutual Funds',
      [
        'ticker',
        'fund_size',
        'price',
        timeframe === '1Y' ? 'trailing_1yr_return' : 'return_5Y_annualized',
        'expense_ratio',
      ],
      { tickerTrack: SEARCH_RESULT_ROW_TRACK },
    ),
    sortable,
  );
}
